
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trophy, Clock, Star } from 'lucide-react';

const LiveResults = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const latestResults = [
    {
      game: 'PowerBall',
      date: '2025-01-23',
      numbers: [7, 14, 23, 31, 42, 45],
      powerball: 12,
      jackpot: '$2,500,000',
      winners: 3,
      isLatest: true
    },
    {
      game: 'Oz Lotto',
      date: '2025-01-21',
      numbers: [3, 18, 26, 33, 39, 44, 8],
      jackpot: '$1,800,000',
      winners: 2,
      isLatest: false
    },
    {
      game: 'Saturday Lotto',
      date: '2025-01-18',
      numbers: [9, 15, 22, 28, 35, 41],
      supplementary: [17, 24],
      jackpot: '$3,200,000',
      winners: 1,
      isLatest: false
    }
  ];

  const nextDraws = [
    {
      game: 'Oz Lotto',
      date: 'Tuesday',
      time: '8:00 PM AEDT',
      jackpot: '$8,000,000',
      timeUntil: getTimeUntil(2, 20) // Tuesday 8 PM
    },
    {
      game: 'PowerBall',
      date: 'Thursday',
      time: '8:00 PM AEDT',
      jackpot: '$5,000,000',
      timeUntil: getTimeUntil(4, 20) // Thursday 8 PM
    },
    {
      game: 'Saturday Lotto',
      date: 'Saturday',
      time: '8:00 PM AEDT',
      jackpot: '$3,000,000',
      timeUntil: getTimeUntil(6, 20) // Saturday 8 PM
    }
  ];

  function getTimeUntil(targetDay: number, targetHour: number) {
    const now = new Date();
    const target = new Date();
    
    // Set target to next occurrence of the day
    const daysUntil = (targetDay - now.getDay() + 7) % 7 || 7;
    target.setDate(now.getDate() + daysUntil);
    target.setHours(targetHour, 0, 0, 0);
    
    // If it's the same day but past the time, move to next week
    if (targetDay === now.getDay() && now.getHours() >= targetHour) {
      target.setDate(target.getDate() + 7);
    }
    
    const timeDiff = target.getTime() - now.getTime();
    const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) {
      return `${days}d ${hours}h`;
    } else {
      const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
      return `${hours}h ${minutes}m`;
    }
  }

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Latest Results */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-6 h-6 text-yellow-600" />
                Latest Results
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {latestResults.map((result, index) => (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h4 className="font-bold text-gray-900 flex items-center gap-2">
                        {result.game}
                        {result.isLatest && <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Latest</Badge>}
                      </h4>
                      <p className="text-sm text-gray-600">{result.date}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-green-600">{result.jackpot}</p>
                      <p className="text-sm text-gray-600">{result.winners} winner{result.winners !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {result.numbers.map((num, idx) => (
                      <div
                        key={idx}
                        className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm font-bold"
                      >
                        {num}
                      </div>
                    ))}
                    {result.powerball && (
                      <>
                        <span className="text-gray-400 mx-2">+</span>
                        <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                          {result.powerball}
                        </div>
                      </>
                    )}
                    {result.supplementary && (
                      <>
                        <span className="text-gray-400 mx-2">Supp:</span>
                        {result.supplementary.map((num, idx) => (
                          <div
                            key={idx}
                            className="w-8 h-8 bg-gray-600 text-white rounded-full flex items-center justify-center text-sm font-bold"
                          >
                            {num}
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Next Draws */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-6 h-6 text-yellow-600" />
                Next Draws
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {nextDraws.map((draw, index) => (
                <div key={index} className="flex justify-between items-center p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div>
                    <h4 className="font-bold text-gray-900">{draw.game}</h4>
                    <p className="text-sm text-gray-600">{draw.date} {draw.time}</p>
                    <p className="text-lg font-bold text-green-600">{draw.jackpot}</p>
                  </div>
                  <div className="text-right">
                    <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                      {draw.timeUntil}
                    </div>
                  </div>
                </div>
              ))}
              
              <div className="text-center pt-4">
                <p className="text-sm text-gray-600">
                  Last updated: {currentTime.toLocaleTimeString()}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default LiveResults;
